import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getJob, getPremiumJob, cancelJob, getJobQueue, type JobQueueResponse, getHairAnalysisJob, getMakeupAnalysisJob, cancelHairAnalysisJob, cancelMakeupAnalysisJob } from '../api/endpoints';
import { usePolling } from '../hooks/usePolling';
import { navigateTo } from '../utils/navigation';
import { postJobView } from '../api/telemetry';
import ProcessingLoader from '../components/ProcessingLoader';

export type PendingJobType = 'base' | 'premium' | 'hair' | 'makeup';

interface PendingPageProps {
  jobType?: PendingJobType;
}

type JobStatus = 'queued' | 'processing' | 'done' | 'error';

function fmtDur(sec: number): string {
  if (sec < 60) return `${Math.round(sec)}s`;
  return `${Math.round(sec / 60)}m`;
}

const HAIR_INSIGHTS = [
  'Analyzing hair texture and density…',
  'Mapping face shape and proportions…',
  'Detecting natural growth patterns…',
  'Evaluating best length and volume ratios…',
  'Generating personalized hairstyle guide…',
];

const MAKEUP_INSIGHTS = [
  'Detecting skin undertone…',
  'Analyzing facial contrast level…',
  'Mapping best foundation shades…',
  'Evaluating eye shape and brow architecture…',
  'Generating personalized makeup guide…',
];

export default function PendingPage({ jobType = 'base' }: PendingPageProps): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [status, setStatus] = useState<JobStatus>('queued');
  const [queuePos, setQueuePos] = useState<number | null>(null);

  const jobId = id ?? '';

  React.useEffect(() => {
    if (jobId && (jobType === 'base' || jobType === 'premium')) {
      const hasTracked = sessionStorage.getItem(`telemetry_job_view_${jobId}`);
      if (!hasTracked) {
        postJobView(jobId).catch(() => { /* telemetry optional */ });
        sessionStorage.setItem(`telemetry_job_view_${jobId}`, '1');
      }
    }
  }, [jobId, jobType]);

  usePolling(
    async () => {
      if (jobType === 'premium') {
        const job = await getPremiumJob(jobId).then(res => res.job);
        setStatus(job.status);
        return job;
      }
      if (jobType === 'hair') {
        const job = await getHairAnalysisJob(jobId).then(res => res.job);
        setStatus(job.status);
        return job;
      }
      if (jobType === 'makeup') {
        const job = await getMakeupAnalysisJob(jobId).then(res => res.job);
        setStatus(job.status);
        return job;
      }
      // base
      const [job, qres] = await Promise.all([
        getJob(jobId),
        getJobQueue(jobId).catch<JobQueueResponse>(() => ({ queue: null })),
      ]);
      setStatus(job.status);
      if (job.status === 'queued' && typeof qres.queue === 'number') {
        setQueuePos(Math.max(1, qres.queue));
      } else {
        setQueuePos(null);
      }
      return job;
    },
    {
      intervalMs: 2000,
      until: (job) => job.status === 'done' || job.status === 'error',
      onFinish: (job) => {
        if (job.status === 'done') {
          if (jobType === 'premium') navigateTo(`/premium-analysis/${jobId}`);
          else if (jobType === 'hair') navigateTo(`/hair-analysis/${jobId}`);
          else if (jobType === 'makeup') navigateTo(`/makeup-analysis/${jobId}`);
          else navigateTo(`/analyses/${jobId}`);
        }
      },
      enabled: !!jobId,
    },
  );

  const handleCancel = async () => {
    if (!jobId) return;
    if (jobType === 'premium') {
      const { cancelPremiumJob } = await import('../api/endpoints');
      await cancelPremiumJob(jobId);
    } else if (jobType === 'hair') {
      await cancelHairAnalysisJob(jobId);
    } else if (jobType === 'makeup') {
      await cancelMakeupAnalysisJob(jobId);
    } else {
      await cancelJob(jobId);
    }
    navigateTo('/');
  };

  const AVG_PER_JOB_SEC = 2;
  const HI_PER_JOB_SEC = 5;
  const SELF_MIN_SEC = jobType === 'hair' || jobType === 'makeup' ? 20 : 5;
  const SELF_MAX_SEC = jobType === 'hair' || jobType === 'makeup' ? 40 : 20;

  const etaRange = useMemo<[number, number] | null>(() => {
    if (status === 'processing') return [SELF_MIN_SEC, SELF_MAX_SEC];
    if (status === 'queued') {
      const ahead = Math.max((queuePos ?? 1) - 1, 0);
      const min = ahead * AVG_PER_JOB_SEC + SELF_MIN_SEC;
      const max = ahead * HI_PER_JOB_SEC + SELF_MAX_SEC + 5;
      return [min, Math.max(min + 5, max)];
    }
    return null;
  }, [status, queuePos, SELF_MIN_SEC, SELF_MAX_SEC]);

  const title = jobType === 'hair'
    ? 'Analyzing your hair…'
    : jobType === 'makeup'
      ? 'Analyzing your features…'
      : 'Analyzing your skin...';

  const subtitle = useMemo(() => {
    if (status === 'queued' && etaRange) {
      return `Est. ${fmtDur(etaRange[0])}–${fmtDur(etaRange[1])}. You can safely leave; the results will appear in History.`;
    }
    if (status === 'processing' && etaRange) {
      return `Working on it now — est. ${fmtDur(etaRange[0])}–${fmtDur(etaRange[1])}. You can safely leave; the results will appear in History.`;
    }
    return jobType === 'hair' || jobType === 'makeup'
      ? 'Our AI is building your personalized guide. Est. 20–40 sec.'
      : 'Please wait while our AI engine processes your image. This usually takes about 15-20 seconds.';
  }, [status, etaRange, jobType]);

  const activeInsights = jobType === 'hair' ? HAIR_INSIGHTS : jobType === 'makeup' ? MAKEUP_INSIGHTS : undefined;

  if (status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-6">
        <div className="w-16 h-16 bg-rose-50 text-rose-500 rounded-full flex items-center justify-center mb-4">
          <span className="material-icons-round text-3xl">error_outline</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h2>
        <p className="text-gray-500 mb-8 max-w-sm">We encountered an error while analyzing your photo. Please try again.</p>
        <button
          onClick={() => { navigateTo('/'); }}
          className="bg-black text-white px-8 py-3 rounded-xl font-medium hover:bg-black/90 transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center w-full max-w-5xl mx-auto">
      <ProcessingLoader
        status={status}
        queuePos={queuePos}
        onCancel={handleCancel}
        title={title}
        subtitle={subtitle}
        insights={activeInsights}
        slowProgress={jobType === 'hair' || jobType === 'makeup'}
      />
      <p className="mt-4 sm:mt-6 text-xs sm:text-sm text-gray-400 flex items-center gap-2 text-center px-4">
        <span className="material-icons-round text-base">info</span>
        You can safely leave this page; the result will appear in your History.
      </p>
    </div>
  );}
